import { create } from "zustand";
import {
  isNewerProgress,
  parseSnapProgress,
  readPersistedProgress,
  type SnapProgressMetadata,
} from "~/types/report-progress";

/**
 * Report progress store — mapa in-memory de progresso por relatório.
 *
 * Alimentado por três fontes, todas convergindo para o MESMO contrato tipado
 * (`SnapProgressMetadata`):
 *
 * 1. frames leves `snap_progress` do WebSocket (chegam antes de existir um
 *    snapshot processado — preservados de propósito);
 * 2. frames `report_partial`, que carregam o progresso validado associado
 *    àquela `sequence`;
 * 3. **rehidratação** a partir de `report_status.snap_progress` quando um
 *    relatório pendente é lido do banco (lista ou detalhe) — é o que faz o
 *    progresso sobreviver a navegação, reload, fechar o browser e reconexão de
 *    WebSocket.
 *
 * Continua **sem persistência própria**: nada de local/session storage. A fonte
 * durável é o banco; esta store é só a projeção em memória.
 *
 * Só contadores — nunca conteúdo de relatório nem PII.
 */
export interface ReportProgressEntry {
  /** Steps the backend reports as completed so far (may be 0/undefined early). */
  completedSteps?: number;
  /** Total steps for the run, when known. */
  totalSteps?: number;
  /** Passos restantes, DERIVADO de total - completed (nunca negativo). */
  remainingSteps?: number;
  /** Fração 0..1 já normalizada no limite de integração. */
  progress?: number;
  /**
   * Sequência da última atualização aceita. Atualizações são monotônicas por
   * ela: sequência menor não regride, sequência igual é idempotente.
   * `undefined` para frames leves `snap_progress`, que não carregam sequência.
   */
  sequence?: number;
  /** True once the final `complete` frame arrived but before the re-render swap. */
  finished: boolean;
}

interface ReportProgressActions {
  /** Apply a `snap_progress` frame, tracked by `user_reports_id`. */
  applyProgress: (reportId: string, entry: Omit<ReportProgressEntry, "finished"> & { finished?: boolean }) => void;
  /** Aplica o metadado tipado de um frame `report_partial` (monotônico). */
  applySnapProgress: (reportId: string, progress: unknown) => void;
  /**
   * Rehidrata a partir do `report_status` persistido de um relatório pendente.
   * Progresso ausente/malformado é ignorado em silêncio — a UI cai no estado
   * indeterminado ("Gerando relatório..."), que é o comportamento anterior.
   */
  rehydrateFromReportStatus: (reportId: string, reportStatus: unknown) => void;
  /** Drop a report's progress once the final render replaces the preview. */
  clearProgress: (reportId: string) => void;
}

interface ReportProgressState {
  progress: Record<string, ReportProgressEntry>;
  actions: ReportProgressActions;
}

/** Entrada resultante de aplicar `parsed` sobre `existing`, ou `null` (no-op). */
function mergeTypedProgress(
  existing: ReportProgressEntry | undefined,
  parsed: SnapProgressMetadata | null,
): ReportProgressEntry | null {
  if (!parsed) return null;
  const stored: SnapProgressMetadata | null =
    existing?.sequence === undefined
      ? null
      : {
          version: 1,
          sequence: existing.sequence,
          completed_steps: existing.completedSteps ?? 0,
        };
  if (!isNewerProgress(stored, parsed)) return null;
  return {
    completedSteps: parsed.completed_steps,
    totalSteps: parsed.total_steps ?? existing?.totalSteps,
    remainingSteps: parsed.remaining_steps ?? existing?.remainingSteps,
    progress: parsed.progress ?? existing?.progress,
    sequence: parsed.sequence,
    finished: existing?.finished ?? false,
  };
}

export const useReportProgressStore = create<ReportProgressState>((set) => ({
  progress: {},
  actions: {
    applyProgress: (reportId, entry) =>
      set((state) => {
        if (!reportId) return state;
        const existing = state.progress[reportId];
        // Lightweight `snap_progress` pings carry no `sequence` (see the
        // interface doc above) — `completedSteps` is the only ordering
        // signal available, and a legitimate progression is monotonically
        // non-decreasing. Without this guard, a reordered/retried webhook
        // ping regresses the LIVE display immediately (found live
        // 2026-08-19: 7/9 shown, then a stale ping dropped it back down) —
        // not just the reload/reconnect case `applySnapProgress`/
        // `rehydrateFromReportStatus` already guard via `mergeTypedProgress`.
        if (
          entry.completedSteps !== undefined &&
          existing?.completedSteps !== undefined &&
          entry.completedSteps < existing.completedSteps
        ) {
          return state;
        }
        const completedSteps = entry.completedSteps ?? existing?.completedSteps;
        const totalSteps = entry.totalSteps ?? existing?.totalSteps;
        return {
          progress: {
            ...state.progress,
            [reportId]: {
              completedSteps,
              totalSteps,
              remainingSteps:
                totalSteps !== undefined && completedSteps !== undefined
                  ? Math.max(totalSteps - completedSteps, 0)
                  : existing?.remainingSteps,
              progress: entry.progress ?? existing?.progress,
              sequence: existing?.sequence,
              finished: entry.finished ?? existing?.finished ?? false,
            },
          },
        };
      }),

    applySnapProgress: (reportId, progress) =>
      set((state) => {
        if (!reportId) return state;
        const merged = mergeTypedProgress(state.progress[reportId], parseSnapProgress(progress));
        if (!merged) return state;
        return { progress: { ...state.progress, [reportId]: merged } };
      }),

    rehydrateFromReportStatus: (reportId, reportStatus) =>
      set((state) => {
        if (!reportId) return state;
        const merged = mergeTypedProgress(
          state.progress[reportId],
          readPersistedProgress(reportStatus),
        );
        if (!merged) return state;
        return { progress: { ...state.progress, [reportId]: merged } };
      }),

    clearProgress: (reportId) =>
      set((state) => {
        if (!reportId || !(reportId in state.progress)) return state;
        const next = { ...state.progress };
        delete next[reportId];
        return { progress: next };
      }),
  },
}));

/** True when a report currently has an active streaming preview in flight. */
export const useReportIsStreaming = (reportId?: string) =>
  useReportProgressStore((state) =>
    reportId ? reportId in state.progress : false
  );

export const useReportProgressEntry = (reportId?: string) =>
  useReportProgressStore((state) =>
    reportId ? state.progress[reportId] : undefined
  );

export const useReportProgressActions = () =>
  useReportProgressStore((state) => state.actions);
